// Background heart rate in the Android app (issue #217, phase 3).
//
// While a workout records, HrRecordingService (mobile/android) keeps its own
// connection to the strap and stores every Heart Rate Measurement with its
// wall-clock time, even if Android stops this page during a call. The recorder
// asks for those samples as it ticks and fills any second it couldn't read
// itself. In a browser every function here is a no-op.
//
// It does the same for an FTMS bike (BikeLink.java), and while this page is
// stopped it sends the strap's HR and the bike's power to the server itself
// (MetricsRelay.java), so the Khronos Timer keeps its numbers with the screen
// off.

import { registerPlugin } from "@capacitor/core";
import { isNativeApp } from "./native";
import { getToken } from "../services/api";

const KhronosRecorder = registerPlugin("KhronosRecorder");

// Samples drained so far this workout, oldest first: { t: epoch ms, bpm, rr? }.
let samples = [];
let lastT = 0;
let startedFor = null; // "strap|bike" the service was started for
let draining = null;
// Bike packets drained so far: { t: epoch ms, w?, c?, sp? (km/h), d?, r?, e?, hr? }.
let bikeSamples = [];
let bikeLastT = 0;

/** True inside the Android app, where background recording exists. */
export const backgroundHrAvailable = () => isNativeApp();

/**
 * Start (or move to another strap or bike) for the workout that's recording.
 * Needs a strap (`deviceId`) or a bike (`bikeId`). Idempotent per pair.
 * `zones` are the HR zone floors the relay sends with heart rate, as the
 * page's own report does. Failures are logged, never thrown: the page's own
 * recording carries on regardless.
 */
export async function startBackgroundHr({ deviceId = null, deviceName = "", bikeId = null, zones = null }) {
  if (!backgroundHrAvailable() || (!deviceId && !bikeId)) return;
  const key = `${deviceId || ""}|${bikeId || ""}`;
  if (startedFor === key) return;
  startedFor = key;
  const relayToken = getToken();
  try {
    await KhronosRecorder.start({
      ...(deviceId ? { deviceId, deviceName: deviceName || "" } : {}),
      ...(bikeId ? { bikeId } : {}),
      ...(relayToken
        ? {
            relayUrl: `${window.location.origin}/api/live/metrics`,
            relayToken,
            ...(Array.isArray(zones) && zones.length ? { relayZones: zones } : {}),
          }
        : {}),
    });
  } catch (err) {
    startedFor = null; // try again next time (e.g. once back in the foreground)
    console.warn("[backgroundHr] start failed", err);
    noteBackgroundHr(`start failed: ${err?.message || err}`);
  }
}

/** Add a line to the app's background log (Developer tools). */
export function noteBackgroundHr(line) {
  if (!backgroundHrAvailable()) return;
  Promise.resolve()
    .then(() => KhronosRecorder.note({ line: String(line) }))
    .catch(() => {});
}

/**
 * The background services' log and state, for Developer tools:
 * { lines: [...], running, linkUp, deviceId, startedAt }, or null outside
 * the app.
 */
export async function backgroundHrDiagnostics() {
  if (!backgroundHrAvailable()) return null;
  return KhronosRecorder.diagnostics();
}

export async function clearBackgroundHrDiagnostics() {
  if (!backgroundHrAvailable()) return;
  await KhronosRecorder.clearDiagnostics();
}

/** The workout ended, was saved, released or discarded. */
export async function stopBackgroundHr() {
  if (!backgroundHrAvailable()) return;
  try {
    await refreshBackgroundHr(); // keep the tail for a finalize that follows
  } catch {
    /* ignore */
  }
  startedFor = null;
  try {
    await KhronosRecorder.stop();
  } catch (err) {
    console.warn("[backgroundHr] stop failed", err);
  }
}

/** Forget this workout's samples (a new recording is starting). */
export function resetBackgroundHr() {
  samples = [];
  lastT = 0;
  bikeSamples = [];
  bikeLastT = 0;
}

/** Pull any samples the service has stored since the last pull. */
export function refreshBackgroundHr() {
  if (!backgroundHrAvailable()) return Promise.resolve();
  if (!draining) {
    draining = Promise.resolve()
      .then(() => KhronosRecorder.drain({ since: lastT, bikeSince: bikeLastT }))
      .then((res) => {
        const fresh = Array.isArray(res?.samples) ? res.samples : [];
        for (const s of fresh) {
          if (typeof s?.t !== "number" || !(s.bpm > 0) || s.t <= lastT) continue;
          samples.push(s);
          lastT = s.t;
        }
        const bike = Array.isArray(res?.bike) ? res.bike : [];
        for (const b of bike) {
          if (typeof b?.t !== "number" || b.t <= bikeLastT) continue;
          bikeSamples.push(b);
          bikeLastT = b.t;
        }
      })
      .catch(() => {})
      .finally(() => {
        draining = null;
      });
  }
  return draining;
}

// The packet nearest a second's midpoint counts if it's within this window.
const MATCH_MS = 1500;

/**
 * Heart rate the strap reported around wall-clock `epochMs`, or null. Picks
 * the sample closest to it within ±1.5 s, as the page's own 3 s staleness
 * window would have.
 */
export function backgroundBpmAt(epochMs) {
  if (!samples.length) return null;
  let lo = 0;
  let hi = samples.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (samples[mid].t < epochMs) lo = mid + 1;
    else hi = mid;
  }
  let best = null;
  for (const i of [lo - 1, lo]) {
    const s = samples[i];
    if (!s) continue;
    const d = Math.abs(s.t - epochMs);
    if (d <= MATCH_MS && (!best || d < best.d)) best = { d, bpm: s.bpm };
  }
  return best ? best.bpm : null;
}

/**
 * Fill heart rate into recorded rows that have none, from the service's
 * samples. `wallAt(t)` maps a row's second to wall-clock ms. Returns how many
 * rows it filled.
 */
export function fillHrFromBackground(rows, wallAt) {
  if (!samples.length || !rows?.length) return 0;
  let filled = 0;
  for (const row of rows) {
    if (row.hr != null) continue;
    const bpm = backgroundBpmAt(wallAt(row.t) + 500);
    if (bpm != null) {
      row.hr = bpm;
      filled += 1;
    }
  }
  return filled;
}

/** The bike packet nearest wall-clock `epochMs` within ±1.5 s, or null. */
function bikeSampleAt(epochMs) {
  if (!bikeSamples.length) return null;
  let lo = 0;
  let hi = bikeSamples.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (bikeSamples[mid].t < epochMs) lo = mid + 1;
    else hi = mid;
  }
  let best = null;
  for (const i of [lo - 1, lo]) {
    const s = bikeSamples[i];
    if (!s) continue;
    const d = Math.abs(s.t - epochMs);
    if (d <= MATCH_MS && (!best || d < best.d)) best = { d, s };
  }
  return best ? best.s : null;
}

/**
 * Fill bike fields into recorded rows the page couldn't read the bike for
 * (no `dev`), from the service's packets, with the same encoding as
 * useTelemetryRecorder's own rows. `air` marks an air bike. Heart rate from
 * the bike only fills a row that still has none. Returns how many rows it
 * filled.
 */
export function fillBikeFromBackground(rows, wallAt, { air = false } = {}) {
  if (!bikeSamples.length || !rows?.length) return 0;
  let filled = 0;
  for (const row of rows) {
    if (row.dev) continue;
    const s = bikeSampleAt(wallAt(row.t) + 500);
    if (!s) continue;
    row.dev = "indoor_bike";
    if (s.w != null) row.w = Math.round(s.w);
    if (air) row.air = 1;
    if (s.c != null) row.c = Math.round(s.c * 10) / 10;
    if (s.sp != null) row.sp = Math.round(s.sp * 10);
    if (s.r != null) row.r = s.r;
    if (s.d != null) row.d = s.d;
    if (s.e != null) row.e = s.e;
    if (row.hr == null && s.hr > 0) row.hr = s.hr;
    filled += 1;
  }
  return filled;
}

/** Test hook: bike packets as drained. */
export const __setBackgroundBikeSamplesForTests = (list) => {
  bikeSamples = list.slice();
  bikeLastT = list.length ? list[list.length - 1].t : 0;
};

/** Test hook. */
export const __setBackgroundSamplesForTests = (list) => {
  samples = list.slice();
  lastT = list.length ? list[list.length - 1].t : 0;
  startedFor = null;
  draining = null;
};
